import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Button, Spinner } from 'reactstrap';
// See dataTable.jsx — Vite pre-bundles react-windowed-select's CJS default
// without unwrapping, so we normalize here.
import WindowedSelectModule from 'react-windowed-select';
const WindowedSelect = WindowedSelectModule.default ?? WindowedSelectModule;
import useTheme from '../../hooks/useTheme';
import { customSelectTheme, selectStyle } from '../utility/selectStyling';
import DatePicker from '../utility/datePicker';
import useCustomersDropdown from '../../hooks/useCustomersDropdown';
import useCreateQuote from '../../hooks/useCreateQuote';

// `customerId` (optional) pre-selects the customer and disables the picker.
// Used by the ViewCustomer page's scoped Quotes table so the shortcut can
// only ever create quotes for the customer on screen.
export default function CreateQuoteModal({ isOpen, onClose, customerId: customerIdProp = null }) {
    const navigate = useNavigate();
    const theme = useTheme();
    const { data: customers } = useCustomersDropdown();
    const createQuote = useCreateQuote();

    // `reset()` puts customerId back to `customerIdProp` on close/submit, so
    // pre-selection survives reopen without needing a sync effect. The modal
    // never remounts between opens — parents toggle `isOpen` — and
    // `customerIdProp` is stable per route, so the initial value is enough.
    const [customerId, setCustomerId] = useState(customerIdProp);

    const [quoteNumber, setQuoteNumber] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [quotedAt, setQuotedAt] = useState((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
    // Only surface validation errors once the user has actually tried to
    // submit — showing red borders on an empty form is noisy.
    const [attempted, setAttempted] = useState(false);

    const customerOptions = (customers ?? []).map(c => ({ value: c.id, label: c.name }));

    const customerError = customerId == null ? 'Customer is required.' : null;
    const titleError    = !title.trim()      ? 'Title is required.'    : null;
    const quotedAtError = !quotedAt          ? 'Quoted date is required.' : null;
    const hasErrors = !!(customerError || titleError || quotedAtError);

    const showCustomerError = attempted && customerError;
    const showTitleError    = attempted && titleError;
    const showQuotedAtError = attempted && quotedAtError;

    const reset = () => {
        setCustomerId(customerIdProp); setQuoteNumber(''); setTitle('');
        setDescription(''); setQuotedAt((() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })());
        setAttempted(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (createQuote.isPending) return;
        if (hasErrors) {
            setAttempted(true);
            return;
        }
        const newId = await createQuote.mutateAsync({
            customerId,
            quoteNumber: quoteNumber.trim() || null,
            title: title.trim(),
            description: description.trim() || null,
            // DatePicker already outputs "yyyy-MM-dd", which the backend
            // binds directly to DateOnly? — no conversion needed.
            quotedAt: quotedAt || null,
        });
        reset();
        onClose();
        navigate(`/quotes/${newId}`);
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="lg">
            <ModalHeader toggle={handleClose}>New Quote</ModalHeader>
            <Form onSubmit={handleSubmit} noValidate>
                <ModalBody>
                    <FormGroup>
                        <Label>Customer *</Label>
                        <WindowedSelect
                            options={customerOptions}
                            value={customerOptions.find(o => o.value === customerId) ?? null}
                            onChange={(opt) => setCustomerId(opt?.value ?? null)}
                            isClearable isSearchable
                            isDisabled={customerIdProp != null}
                            styles={selectStyle}
                            theme={customSelectTheme(theme)}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="Select a customer..."
                        />
                        {showCustomerError && (
                            <div className="invalid-feedback d-block">{customerError}</div>
                        )}
                    </FormGroup>
                    <FormGroup>
                        <Label>Quote #</Label>
                        <Input
                            value={quoteNumber}
                            onChange={(e) => setQuoteNumber(e.target.value)}
                            placeholder="Auto-generated if blank..."
                            className="placeholder-italic"
                        />
                    </FormGroup>
                    <FormGroup>
                        <Label>Title *</Label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            invalid={!!showTitleError}
                        />
                        <FormFeedback>{titleError}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>Description</Label>
                        <Input type="textarea" rows={3} value={description}
                               onChange={(e) => setDescription(e.target.value)} />
                    </FormGroup>
                    <FormGroup>
                        <Label>Quoted On *</Label>
                        <DatePicker
                            value={quotedAt}
                            handleChange={setQuotedAt}
                            isClearable
                            inputStyle={showQuotedAtError ? { borderColor: 'var(--bs-danger)' } : undefined}
                        />
                        {showQuotedAtError && (
                            <div className="invalid-feedback d-block">{quotedAtError}</div>
                        )}
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={handleClose}
                            disabled={createQuote.isPending}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={createQuote.isPending}>
                        {createQuote.isPending && <Spinner size="sm" className="me-1" />}
                        Create
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
